// Sign-Off Panel — PSD §8.8 (F8.8). Report screen.

import { useState } from 'react';
import type { SignOffRecord } from '../../persistence/schema';
import { useCanSignOff, useSettings } from '../../store/selectors';
import { useAlert } from '@ascend/alert';

interface SignOffPanelProps {
  signOff: SignOffRecord | null;
  onSubmit: (annotations: string, approved: boolean) => void;
}

export function SignOffPanel({ signOff, onSubmit }: SignOffPanelProps) {
  const canSignOff = useCanSignOff(null);
  const settings = useSettings();
  const { confirm, alert } = useAlert();
  const [annotations, setAnnotations] = useState('');

  if (signOff) {
    return (
      <div className="card signoff-panel">
        <h3>Reviewer Sign-Off</h3>
        <div className="signoff-record">
          <p>
            <strong>Status:</strong>{' '}
            <span className={`badge badge-${signOff.approved ? 'success' : 'error'}`}>
              {signOff.approved ? 'Approved' : 'Rejected'}
            </span>
          </p>
          <p>
            <strong>Signed by:</strong> {signOff.signedOffBy} ({signOff.role})
          </p>
          <p>
            <strong>Date:</strong> {signOff.signedOffAt}
          </p>
          {signOff.annotations && (
            <p>
              <strong>Annotations:</strong> {signOff.annotations}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!canSignOff) {
    return (
      <div className="card signoff-panel">
        <h3>Reviewer Sign-Off</h3>
        <p className="field-hint">
          Your role ({settings.rbacRole}) does not have sign-off permission. Only reviewers and approvers can sign off.
        </p>
      </div>
    );
  }

  const handleApprove = async () => {
    const ok = await confirm({
      title: 'Approve & Finalize?',
      message:
        'This will lock the valuation record as finalized. ' +
        'Further edits to inputs will require a new valuation.',
      confirmLabel: 'Approve & Finalize',
      type: 'info',
    });
    if (!ok) return;
    onSubmit(annotations, true);
    await alert({
      title: 'Valuation approved',
      message: 'The valuation has been finalized and signed off.',
      type: 'success',
    });
  };

  const handleReject = async () => {
    const ok = await confirm({
      title: 'Reject valuation?',
      message:
        'A rejection records a reviewer dissent on this valuation record. ' +
        'This action cannot be undone.',
      confirmLabel: 'Reject',
      danger: true,
      type: 'danger',
    });
    if (!ok) return;
    onSubmit(annotations, false);
  };

  return (
    <div className="card signoff-panel">
      <h3>Reviewer Sign-Off</h3>
      <div className="form-group">
        <label htmlFor="annotations">Annotations</label>
        <textarea
          id="annotations"
          rows={3}
          placeholder="Review notes, conditions, or rationale..."
          value={annotations}
          onChange={(e) => setAnnotations(e.target.value)}
        />
      </div>
      <div className="form-row">
        <button className="btn btn-primary" onClick={handleApprove}>
          Approve &amp; Finalize
        </button>
        <button className="btn btn-secondary" onClick={handleReject}>
          Reject
        </button>
      </div>
    </div>
  );
}
